import { ModuleDocContent } from "./docsData";

export const certificationModuleDocs: Record<string, ModuleDocContent> = {
  "certification-builder": {
    title: "Certification Builder",
    subtitle: "AI-Powered Certification Creator",
    color: "violet",
    badge: "Certification Management",
    description:
      "Create certifications using AI. Describe the certification goal, link prerequisite courses and learning paths, configure exam settings, and the AI generates the full certification including metadata, images, and exam questions.",
    content: `
      <div class="sz-callout sz-callout-info">
        <p>
          <strong>AI Certification Builder</strong> lets you create complete certifications from a text description. The AI generates the certification name, description, slug, learning outcomes, tags, and exam questions automatically. Link existing courses and learning paths as prerequisites, and configure exam rules before generating.
        </p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/e5a5520a-3cc6-4b3b-8e9e-25880212629c.png" alt="Certification Builder" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: AI certification creation form</p></div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/c484096f-cf9d-489f-9c05-d1a002515967.png" alt="Certification List" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Certification list overview</p></div>

      <section>
        <h2>How it works</h2>
        <div class="sz-steps">
          <div class="sz-step">
            <div class="sz-step-num">1</div>
            <div>
              <div class="sz-step-title">Describe your certification</div>
              <div class="sz-step-desc">
                Enter a certification description (100–1,000 characters). Click <strong>Enhance with AI</strong> to refine your input. No certification name is needed — the AI generates a professional name, URL-friendly slug, description, and learning outcomes automatically.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">2</div>
            <div>
              <div class="sz-step-title">Link prerequisite courses and learning paths</div>
              <div class="sz-step-desc">
                Use the multi-select dropdowns to link courses and/or learning paths that learners must complete before attempting the exam. At least one course or learning path is required.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">3</div>
            <div>
              <div class="sz-step-title">Configure exam settings</div>
              <div class="sz-step-desc">
                Set total questions, question types (Single Choice, Multiple Choice, True/False, Text Answer), time limit, passing score %, and difficulty distribution (Easy / Medium / Hard must total 100%). Toggle options for shuffle, feedback, retry, and max attempts.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">4</div>
            <div>
              <div class="sz-step-title">Generate and track</div>
              <div class="sz-step-desc">
                Click <strong>Generate Certification with AI</strong>. The certification appears in the Recent Certifications section with real-time progress tracking across three stages: Metadata, Images, and Questions.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2>Exam Configuration</h2>
        <table>
          <thead>
            <tr>
              <th>Setting</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Total Questions</strong></td>
              <td>Number of exam questions to generate.</td>
            </tr>
            <tr>
              <td><strong>Question Types</strong></td>
              <td>Multi-select: Single Choice, Multiple Choice, True/False, Text Answer.</td>
            </tr>
            <tr>
              <td><strong>Time Limit</strong></td>
              <td>Maximum duration in minutes for exam completion.</td>
            </tr>
            <tr>
              <td><strong>Passing Score</strong></td>
              <td>Minimum percentage required to pass the exam.</td>
            </tr>
            <tr>
              <td><strong>Difficulty Distribution</strong></td>
              <td>Percentage split across Easy, Medium, and Hard. Must total 100%.</td>
            </tr>
            <tr>
              <td><strong>Shuffle Questions / Options</strong></td>
              <td>Randomize question and answer order per attempt.</td>
            </tr>
            <tr>
              <td><strong>Show Feedback / Correct Answers</strong></td>
              <td>Control what learners see after completing the exam.</td>
            </tr>
            <tr>
              <td><strong>Allow Retry / Max Attempts</strong></td>
              <td>Whether learners can retake the exam, and the maximum number of attempts.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Certification List</h2>
        <p>The Certification Catalog page displays all certifications with:</p>
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Grid / List view toggle</td>
              <td>Switch between card grid and compact list view.</td>
            </tr>
            <tr>
              <td>Search</td>
              <td>Real-time search with debounce across certification titles.</td>
            </tr>
            <tr>
              <td>Status filter</td>
              <td>Filter by Published or Unpublished.</td>
            </tr>
            <tr>
              <td>Sort options</td>
              <td>10 sort options including Recently Updated, Newest, Title A–Z, Time Limit, and more.</td>
            </tr>
            <tr>
              <td>Card metadata</td>
              <td>Star rating, time limit, passing score, total points, question count, and max attempts.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Professional certification program</div>
            <div class="sz-use-case-flow">
              Describe the certification goal, link foundational courses, set an 80% passing score with Hard difficulty weighting at 40%. The AI generates the certification with exam questions matching your criteria.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Quick compliance certification</div>
            <div class="sz-use-case-flow">
              Use <strong>Enhance with AI</strong> on a brief compliance topic description. Link the required compliance course, set True/False and Single Choice question types, and generate. Publish the same day.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>
          Ensure your difficulty distribution adds up to 100% before generating. Start with 40% Easy, 40% Medium, 20% Hard for balanced exams.
        </p>
      </div>
    `,
  },
};
